import { action } from '@ember/object';
import { service } from '@ember/service';

import type { Service } from 'webapp-types';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';
import BoardWallComponent from 'webapp/components/board/wall';
import ReportsSkeletonFragmentComponent from 'webapp/components/reports/skeleton-fragment';
import ReportsViewerFragmentBoardComponent from 'webapp/components/reports/viewer/fragment/board';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type ReportSlideFragment from 'webapp/models-custom/report-slide-fragment';

interface Args {
	model: ReportSlideFragment;
	width?: number;
}

export default class ReportsEditorFragmentBoardComponent extends ReportsViewerFragmentBoardComponent<Args> {
	@service('model-manager/report-slide-fragments')
	declare mmReportSlideFragmentsService: Service.ModelManager.ReportSlideFragments;

	get showDesignerLink(): boolean {
		return !!this.args.model.isOnPagelessReport;
	}

	@action
	override onModelChange(isInitialCheck = false): void {
		super.onModelChange(isInitialCheck);

		const boardState = this.boardState,
			fragment = this.args.model;

		if (boardState) {
			boardState.isReportEditor = true;
			boardState.onPreviewHashChange = (previewHash) => {
				this.previewHash = previewHash;

				if (fragment) {
					fragment.updatePreviewHash(previewHash);
					void fragment.saveAndCatch();
				}
			};
		}
	}

	@action
	chooseBoard(): void {
		const popup = this.mmBoardsService.createBoardsSelectListModal();

		popup.open({
			model: this.mmBoardsService.normalBoardModels,
			maxSelectable: 1,
			isReport: true,
			onConfirm: async (selected: number[]) => {
				const boardId = selected[0];
				if (boardId) {
					const fragment = this.args.model;
					fragment.referenceId = boardId;
					void fragment.saveAndCatch();
				}
				return Promise.resolve(true);
			},
		});
	}
	<template>
		<div
			class="fragment fragment--board {{if this.isEmpty 'fragment--board--empty'}}"
			{{didInsert this.setupMasterDateRangeEvent}}
			{{didUpdate this.handleModelChangeEvent @model.boardOrPreview}}
			{{willDestroy this.teardownMasterDateRangeEvent}}
			style={{this.emptyBoardStyles}}
		>
			{{#if this.isEmpty}}
				<svg>
					<use
						{{! @glint-ignore: xlink:href is not typed on SVGUseElement in Glint 2 }}
						xlink:href="/images/reports/slide-type-board.svg#img"
						width="100%"
						height="100%"
					></use>
				</svg>
				<span>
					No dashboard is selected yet.
				</span>
				<SubmitButtonComponent
					@text="Browse dashboards..."
					@size="big"
					@noDelay={{true}}
					@onTrigger={{this.chooseBoard}}
				/>
			{{else if this.boardState}}
				<BoardWallComponent
					@boardState={{this.boardState}}
					@inCustomContainer={{this.customContainer}}
					@trackingOrigin="report_editor"
					@showDesignerLink={{this.showDesignerLink}}
				/>
			{{else}}
				<ReportsSkeletonFragmentComponent @type={{this.skeletonType}} />
			{{/if}}
		</div>
	</template>
}
